import React, { useState } from 'react'
import { LogPagination as PaginationType } from '../types'
import useTranslate from '../useTranslate'
import useStyles from '../styles'

interface LogPaginationProps {
  pagination: PaginationType
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
}

const PAGE_SIZE_OPTIONS = [25, 50, 75, 100]

function getPageNumbers(current: number, total: number): Array<number | '...'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)

  const pages: Array<number | '...'> = [1]

  if (current > 3) pages.push('...')

  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)

  for (let i = start; i <= end; i++) pages.push(i)

  if (current < total - 2) pages.push('...')

  pages.push(total)
  return pages
}

const LogPaginationComponent: React.FC<LogPaginationProps> = ({
  pagination,
  onPageChange,
  onLimitChange,
}) => {
  const classes = useStyles()
  const t = useTranslate()
  const { page, totalPages, limit } = pagination
  const [goToValue, setGoToValue] = useState('')

  const pages = getPageNumbers(page, totalPages)

  const commitGoTo = (): void => {
    const parsed = parseInt(goToValue, 10)
    setGoToValue('')
    if (Number.isNaN(parsed)) return
    const target = Math.min(Math.max(parsed, 1), totalPages)
    if (target !== page) onPageChange(target)
  }

  return (
    <nav className={classes.pagination}>
      <div className={classes.pageSize}>
        <span>{t('log_viewer.pagination.show')}</span>
        <select
          className={classes.pageSizeSelect}
          value={limit}
          onChange={e => onLimitChange(Number(e.target.value))}
          aria-label={t('log_viewer.pagination.rows_per_page')}
        >
          {PAGE_SIZE_OPTIONS.map(size => (
            <option
              key={size}
              value={size}
            >
              {size}
            </option>
          ))}
        </select>
        <span>{t('log_viewer.pagination.per_page')}</span>
      </div>

      {totalPages > 1 && (
        <>
          <button
            className={`${classes.pageButton} ${
              page <= 1 ? classes.pageButtonDisabled : ''
            }`}
            onClick={() => page > 1 && onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <span
              className='material-symbols-outlined'
              style={{ fontSize: 16 }}
            >
              chevron_left
            </span>
            {t('log_viewer.pagination.previous')}
          </button>

          {pages.map((p, i) =>
            p === '...' ? (
              <span
                key={`ellipsis-${i}`}
                className={classes.pageEllipsis}
              >
                <span
                  className='material-symbols-outlined'
                  style={{ fontSize: 16 }}
                >
                  more_horiz
                </span>
              </span>
            ) : (
              <button
                key={p}
                className={`${classes.pageButton} ${
                  p === page ? classes.pageButtonActive : ''
                }`}
                onClick={() => onPageChange(p)}
              >
                {p}
              </button>
            ),
          )}

          <button
            className={`${classes.pageButton} ${
              page >= totalPages ? classes.pageButtonDisabled : ''
            }`}
            onClick={() => page < totalPages && onPageChange(page + 1)}
            disabled={page >= totalPages}
          >
            {t('log_viewer.pagination.next')}
            <span
              className='material-symbols-outlined'
              style={{ fontSize: 16 }}
            >
              chevron_right
            </span>
          </button>

          <div className={classes.goToPage}>
            <span>{t('log_viewer.pagination.go_to')}</span>
            <input
              type='number'
              min={1}
              max={totalPages}
              className={classes.goToPageInput}
              value={goToValue}
              onChange={e => setGoToValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && commitGoTo()}
              onBlur={commitGoTo}
              aria-label={t('log_viewer.pagination.go_to_page')}
            />
            <span>{t('log_viewer.pagination.page')}</span>
          </div>
        </>
      )}
    </nav>
  )
}

export default LogPaginationComponent
